import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { FitDuplicateReview, FitDuplicateReviewService } from './fit-duplicate-review.service';

export interface FitDuplicateReviewDialogData {
  reviews: FitDuplicateReview[];
}

export interface FitDuplicateReviewDialogResult {
  resolved: number;   // wie viele Reviews bearbeitet wurden (für Refresh)
}

/** Liste der automatisch erkannten möglichen Dubletten. Pro Eintrag kann der Nutzer
 *  die importierte Aktivität BEHALTEN (Markierung weg) oder VERWERFEN (Session+Datei
 *  löschen) — nach Prüfung der bestehenden Treffer. */
@Component({
  standalone: true,
  selector: 'app-fit-duplicate-review-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    <h2 mat-dialog-title>Mögliche Dubletten prüfen</h2>
    <mat-dialog-content>
      @if (!rows.length) {
        <p class="fr-empty">Keine offenen Dubletten.</p>
      }
      @for (r of rows; track r.file_id) {
        <div class="fr-card" [class.fr-busy]="busy === r.file_id">
          <div class="fr-head">
            <mat-icon>content_copy</mat-icon>
            <div class="fr-headtxt">
              <strong>Neu importiert{{ r.date ? ' — ' + r.date : '' }}</strong>
              <span class="fr-sub">{{ r.filename }}@if (r.source) { · {{ r.source }} }</span>
            </div>
          </div>
          <div class="fr-existing">
            <span class="fr-existing__label">Bereits vorhanden:</span>
            <ul>
              @for (c of r.candidates; track c.file_id) {
                <li [class.fr-other]="c.is_other_user">
                  {{ c.athlete_label }}@if (c.date) { — {{ c.date }} }@if (c.source) { ({{ c.source }}) }
                  @if (c.is_other_user) { <span class="fr-warn">⚠ anderer Sportler</span> }
                </li>
              }
            </ul>
          </div>
          <div class="fr-actions">
            <button mat-stroked-button color="warn" [disabled]="busy" (click)="resolve(r, 'discard')">
              Verwerfen
            </button>
            <button mat-stroked-button [disabled]="busy" (click)="resolve(r, 'keep')">
              Behalten
            </button>
            @if (busy === r.file_id) { <mat-spinner diameter="18"></mat-spinner> }
          </div>
        </div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-flat-button color="primary" (click)="close()">Fertig</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .fr-empty { color: var(--text-secondary, #6c757d); }
    .fr-card { border: 1px solid var(--border-color, #e0e0e0); border-radius: var(--radius-sm, 6px);
      padding: 0.6rem 0.75rem; margin-bottom: 0.6rem; }
    .fr-card.fr-busy { opacity: 0.6; }
    .fr-head { display: flex; gap: 0.5rem; align-items: flex-start; }
    .fr-head mat-icon { color: var(--color-warning, #d97706); flex: 0 0 auto; }
    .fr-headtxt { display: flex; flex-direction: column; min-width: 0; }
    .fr-sub { color: var(--text-secondary, #6c757d); font-size: var(--fs-sm, 0.875rem); word-break: break-all; }
    .fr-existing { margin: 0.4rem 0 0.5rem; font-size: var(--fs-sm, 0.875rem); }
    .fr-existing__label { color: var(--text-secondary, #6c757d); }
    .fr-existing ul { margin: 0.2rem 0 0; padding-left: 1.1rem; }
    .fr-other { color: var(--status-warning-text, #92400e); }
    .fr-warn { font-weight: 600; }
    .fr-actions { display: flex; gap: 0.5rem; align-items: center; }
  `],
})
export class FitDuplicateReviewDialogComponent {
  private readonly svc = inject(FitDuplicateReviewService);
  private readonly cdr = inject(ChangeDetectorRef);

  rows: FitDuplicateReview[];
  busy: string | null = null;
  private resolved = 0;

  constructor(
    readonly dialogRef: MatDialogRef<FitDuplicateReviewDialogComponent, FitDuplicateReviewDialogResult>,
    @Inject(MAT_DIALOG_DATA) data: FitDuplicateReviewDialogData,
  ) {
    this.rows = [...(data.reviews ?? [])];
  }

  resolve(r: FitDuplicateReview, action: 'keep' | 'discard'): void {
    if (this.busy) { return; }
    this.busy = r.file_id;
    this.cdr.markForCheck();
    this.svc.resolve(r.file_id, action).subscribe({
      next: () => {
        this.rows = this.rows.filter((x) => x.file_id !== r.file_id);
        this.resolved += 1;
        this.busy = null;
        this.cdr.markForCheck();
      },
      error: () => { this.busy = null; this.cdr.markForCheck(); },
    });
  }

  close(): void {
    this.dialogRef.close({ resolved: this.resolved });
  }
}
